/**
 * Image Metadata and Watermarking Service
 * Adds security metadata and watermarks to images for traceability and fingerprinting
 */

import { auth } from '@/firebase/config';

export interface ImageMetadata {
    wallpaperId: string;
    userId?: string;
    userEmail?: string;
    downloadTimestamp: number;
    downloadId: string;
    userAgent: string;
    ipFingerprint: string;
    sessionId: string;
}

export interface WatermarkOptions {
    text?: string;
    position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
    opacity?: number;
    fontSize?: number;
    color?: string;
    includeTimestamp?: boolean;
    includeUserId?: boolean;
}

export interface ProcessedImageResult {
    blob: Blob;
    metadata: ImageMetadata;
    fingerprint: string;
}

class ImageMetadataService {
    private readonly DEFAULT_WATERMARK_OPTIONS: Required<WatermarkOptions> = {
        text: '',
        position: 'bottom-right',
        opacity: 0.7,
        fontSize: 14,
        color: '#ffffff',
        includeTimestamp: true,
        includeUserId: true
    };

    /**
     * Process image with metadata and watermark for download
     */
    async processImageForDownload(
        imageUrl: string,
        wallpaperId: string,
        watermarkOptions: Partial<WatermarkOptions> = {}
    ): Promise<ProcessedImageResult> {
        const metadata = this.generateMetadata(wallpaperId);
        const options = { ...this.DEFAULT_WATERMARK_OPTIONS, ...watermarkOptions };

        try {
            const blob = await this.addMetadataAndWatermark(imageUrl, metadata, options);
            const fingerprint = await this.generateImageFingerprint(blob, metadata);

            return {
                blob,
                metadata,
                fingerprint
            };
        } catch (error) {
            console.error('[ImageMetadataService] Failed to process image:', error);
            throw new Error('Failed to process image for secure download');
        }
    }

    /**
     * Generate comprehensive metadata for the download
     */
    private generateMetadata(wallpaperId: string): ImageMetadata {
        const user = auth.currentUser;
        const downloadId = this.generateDownloadId();
        const sessionId = this.getOrCreateSessionId();

        return {
            wallpaperId,
            userId: user?.uid,
            userEmail: user?.email || undefined,
            downloadTimestamp: Date.now(),
            downloadId,
            userAgent: navigator.userAgent,
            ipFingerprint: this.generateIpFingerprint(),
            sessionId
        };
    }

    /**
     * Add metadata and watermark to image
     */
    private async addMetadataAndWatermark(
        imageUrl: string,
        metadata: ImageMetadata,
        options: Required<WatermarkOptions>
    ): Promise<Blob> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';

            img.onload = () => {
                try {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    if (!ctx) {
                        reject(new Error('Canvas context not available'));
                        return;
                    }

                    // Set canvas size to original image size
                    canvas.width = img.width;
                    canvas.height = img.height;

                    // Draw original image
                    ctx.drawImage(img, 0, 0);

                    // Add watermark
                    this.addWatermark(ctx, canvas.width, canvas.height, metadata, options);

                    // Add invisible metadata (steganography-like approach)
                    this.embedInvisibleMetadata(ctx, canvas.width, canvas.height, metadata);

                    // Convert to blob with high quality
                    canvas.toBlob((blob) => {
                        if (blob) {
                            resolve(blob);
                        } else {
                            reject(new Error('Failed to create blob from canvas'));
                        }
                    }, 'image/jpeg', 0.95);

                } catch (error) {
                    reject(error);
                }
            };

            img.onerror = () => {
                reject(new Error('Failed to load image'));
            };

            img.src = imageUrl;
        });
    }

    /**
     * Add visible watermark to image
     */
    private addWatermark(
        ctx: CanvasRenderingContext2D,
        width: number,
        height: number,
        metadata: ImageMetadata,
        options: Required<WatermarkOptions>
    ): void {
        ctx.save();

        // Calculate responsive font size
        const baseFontSize = Math.max(12, Math.min(width, height) * 0.015);
        const fontSize = options.fontSize || baseFontSize;

        // Set watermark style
        ctx.globalAlpha = options.opacity;
        ctx.fillStyle = options.color;
        ctx.font = `${fontSize}px Arial, sans-serif`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';

        // Add text shadow for better visibility
        ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
        ctx.shadowBlur = 2;
        ctx.shadowOffsetX = 1;
        ctx.shadowOffsetY = 1;

        // Build watermark text
        const watermarkLines = this.buildWatermarkText(metadata, options);

        // Calculate position
        const padding = fontSize * 0.8;
        const lineHeight = fontSize * 1.2;
        let x: number, y: number;

        switch (options.position) {
            case 'top-left':
                x = padding;
                y = padding;
                break;
            case 'top-right':
                ctx.textAlign = 'right';
                x = width - padding;
                y = padding;
                break;
            case 'bottom-left':
                x = padding;
                y = height - (watermarkLines.length * lineHeight) - padding;
                break;
            case 'bottom-right':
            default:
                ctx.textAlign = 'right';
                x = width - padding;
                y = height - (watermarkLines.length * lineHeight) - padding;
                break;
        }

        // Draw watermark lines
        watermarkLines.forEach((line, index) => {
            ctx.fillText(line, x, y + (index * lineHeight));
        });

        ctx.restore();
    }

    /**
     * Build watermark text based on options and metadata
     */
    private buildWatermarkText(_metadata: ImageMetadata, _options: Required<WatermarkOptions>): string[] {
        const lines: string[] = [];

        // Always show WallpaperHub branding
        lines.push('WallpaperHub');

        return lines;
    }

    /**
     * Embed invisible metadata using steganography-like technique
     * This embeds metadata in the least significant bits of pixels
     */
    private embedInvisibleMetadata(
        ctx: CanvasRenderingContext2D,
        width: number,
        height: number,
        metadata: ImageMetadata
    ): void {
        try {
            // Get image data
            const imageData = ctx.getImageData(0, 0, width, height);
            const data = imageData.data;

            // Convert metadata to binary string
            const metadataString = JSON.stringify({
                id: metadata.downloadId,
                wid: metadata.wallpaperId,
                uid: metadata.userId?.substring(0, 16), // Truncate for space
                ts: metadata.downloadTimestamp,
                ua: this.hashString(metadata.userAgent).substring(0, 16) // Hash and truncate
            });

            const binaryString = this.stringToBinary(metadataString);

            // Embed in LSB of red channel of first few pixels
            const maxPixels = Math.min(binaryString.length, data.length / 4);

            for (let i = 0; i < maxPixels; i++) {
                const pixelIndex = i * 4; // RGBA, so every 4th value
                const bit = parseInt(binaryString[i]);

                // Modify LSB of red channel
                data[pixelIndex] = (data[pixelIndex] & 0xFE) | bit;
            }

            // Put modified image data back
            ctx.putImageData(imageData, 0, 0);
        } catch (error) {
            console.warn('[ImageMetadataService] Failed to embed invisible metadata:', error);
            // Continue without invisible metadata if it fails
        }
    }

    /**
     * Generate unique download ID
     */
    private generateDownloadId(): string {
        const timestamp = Date.now().toString(36);
        const random = Math.random().toString(36).substring(2, 8);
        return `${timestamp}-${random}`;
    }

    /**
     * Get or create session ID
     */
    private getOrCreateSessionId(): string {
        const key = 'wallpaper_session_id';
        let sessionId = sessionStorage.getItem(key);

        if (!sessionId) {
            sessionId = this.generateDownloadId();
            sessionStorage.setItem(key, sessionId);
        }

        return sessionId;
    }

    /**
     * Generate IP fingerprint (client-side approximation)
     */
    private generateIpFingerprint(): string {
        // Create a fingerprint based on available client info
        const fingerprint = [
            navigator.userAgent,
            navigator.language,
            screen.width + 'x' + screen.height,
            new Date().getTimezoneOffset(),
            'browser-fingerprint'
        ].join('|');

        return this.hashString(fingerprint).substring(0, 16);
    }

    /**
     * Generate image fingerprint for traceability
     */
    private async generateImageFingerprint(blob: Blob, metadata: ImageMetadata): Promise<string> {
        try {
            // Create fingerprint from blob and metadata
            const blobHash = await this.hashBlob(blob);
            const metadataHash = this.hashString(JSON.stringify(metadata));

            return `${blobHash.substring(0, 16)}-${metadataHash.substring(0, 16)}`;
        } catch (error) {
            console.warn('[ImageMetadataService] Failed to generate fingerprint:', error);
            return this.generateDownloadId();
        }
    }

    /**
     * Hash a string using a simple hash function
     */
    private hashString(str: string): string {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32-bit integer
        }
        return Math.abs(hash).toString(36);
    }

    /**
     * Hash a blob using Web Crypto API
     */
    private async hashBlob(blob: Blob): Promise<string> {
        try {
            const arrayBuffer = await blob.arrayBuffer();
            const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        } catch (error) {
            // Fallback to simple hash
            return this.hashString(blob.size + blob.type + Date.now());
        }
    }

    /**
     * Convert string to binary representation
     */
    private stringToBinary(str: string): string {
        return str.split('').map(char =>
            char.charCodeAt(0).toString(2).padStart(8, '0')
        ).join('');
    }

    /**
     * Extract metadata from image (for verification)
     */
    async extractMetadataFromImage(imageBlob: Blob): Promise<Partial<ImageMetadata> | null> {
        try {
            return new Promise((resolve) => {
                const img = new Image();
                const url = URL.createObjectURL(imageBlob);

                img.onload = () => {
                    try {
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');

                        if (!ctx) {
                            resolve(null);
                            return;
                        }

                        canvas.width = img.width;
                        canvas.height = img.height;
                        ctx.drawImage(img, 0, 0);

                        // Extract from LSB
                        const imageData = ctx.getImageData(0, 0, img.width, img.height);
                        const data = imageData.data;

                        let binaryString = '';
                        const maxPixels = Math.min(1000, data.length / 4); // Limit extraction

                        for (let i = 0; i < maxPixels; i++) {
                            const pixelIndex = i * 4;
                            binaryString += (data[pixelIndex] & 1).toString();
                        }

                        // Convert binary to string and parse JSON
                        const extractedString = this.binaryToString(binaryString);
                        const metadata = JSON.parse(extractedString);

                        resolve(metadata);
                    } catch (error) {
                        console.warn('[ImageMetadataService] Failed to extract metadata:', error);
                        resolve(null);
                    } finally {
                        URL.revokeObjectURL(url);
                    }
                };

                img.onerror = () => {
                    URL.revokeObjectURL(url);
                    resolve(null);
                };

                img.src = url;
            });
        } catch (error) {
            console.warn('[ImageMetadataService] Metadata extraction failed:', error);
            return null;
        }
    }

    /**
     * Convert binary string back to text
     */
    private binaryToString(binary: string): string {
        const bytes = binary.match(/.{8}/g) || [];
        return bytes.map(byte => String.fromCharCode(parseInt(byte, 2))).join('');
    }

    /**
     * Log download for audit trail
     */
    logDownload(metadata: ImageMetadata, fingerprint: string): void {
        const logEntry = {
            ...metadata,
            fingerprint,
            loggedAt: Date.now()
        };

        // Store in localStorage for audit (in production, send to server)
        const logs = JSON.parse(localStorage.getItem('download_audit_log') || '[]');
        logs.push(logEntry);

        // Keep only last 100 entries
        if (logs.length > 100) {
            logs.splice(0, logs.length - 100);
        }

        localStorage.setItem('download_audit_log', JSON.stringify(logs));

        console.log('[ImageMetadataService] Download logged:', {
            downloadId: metadata.downloadId,
            wallpaperId: metadata.wallpaperId,
            userId: metadata.userId,
            fingerprint
        });
    }

    /**
     * Get download audit logs
     */
    getDownloadLogs(): (ImageMetadata & { fingerprint: string; loggedAt: number })[] {
        return JSON.parse(localStorage.getItem('download_audit_log') || '[]');
    }

    /**
     * Clear download logs (admin function)
     */
    clearDownloadLogs(): void {
        localStorage.removeItem('download_audit_log');
        console.log('[ImageMetadataService] Download logs cleared');
    }
}

export const imageMetadataService = new ImageMetadataService();

// Make available globally for testing
if (process.env.NODE_ENV === 'development') {
    (window as any).imageMetadataService = imageMetadataService;
}
